import { Tag } from "entities/tags/types";
import { useThemePrimaryTagsQuery } from "entities/themeTagTypes/queries/useThemePrimaryTagsQuery";
import { useThemeTagsListQuery } from "entities/themeTags/queries/useThemeTagListQuery";
import groupBy from "lodash/groupBy";
import { useCallback, useMemo, useState } from "react";
import { ComboGroupedElement } from "shared/components/Combobox/type";
import { getFilteredTagsBySearchValue } from "../utils";

export type ThemeTagFilterApi = {
  selectedThemeTags: string[];
  handleSelectGroupedOptions: (values: string[]) => void;
  handleClearSelectedOptions: () => void;
  groupedThemeTags: ComboGroupedElement[];
  themeTagsFilterGames: string;
  themeTagsSearchQuery: string;
  handleSearchThemeTags: (query: string) => void;
};

export const useThemeTagsFilter = (): ThemeTagFilterApi => {
  const [searchValue, setSearchValue] = useState("");
  const [selectedThemeTagValues, setSelectedThemeTagValues] = useState<
    string[]
  >([]);
  const [searchedTags, setSearchedTags] = useState<ComboGroupedElement[]>([]);

  const { data: themePrimaryTags = [] } = useThemePrimaryTagsQuery();
  const { data: themeTags = [] } = useThemeTagsListQuery();

  const themeTagsByPrimaryTag = useMemo(() => {
    const tags: Record<string, Tag[]> = groupBy(themeTags, "tagType");

    return tags;
  }, [themeTags]);

  const groupedThemeTags: ComboGroupedElement[] = useMemo(() => {
    const primaryTagsMap = new Map(
      themePrimaryTags?.map((tag) => [tag.id, tag.name]),
    );

    return Object.entries(themeTagsByPrimaryTag)?.map(([id, tags]) => ({
      value: id,
      name: primaryTagsMap.get(id) || id,
      options: tags?.map((tag) => ({
        value: tag.id,
        name: tag.name,
      })),
    }));
  }, [themePrimaryTags, themeTagsByPrimaryTag]);

  const themeTagsFilterGames = useMemo(() => {
    const tagsFilter =
      selectedThemeTagValues.length > 0
        ? `(${selectedThemeTagValues?.map((id) => `themeTags:${id}`).join(" OR ")})`
        : "";

    return [tagsFilter]?.filter(Boolean)?.join(" OR ");
  }, [selectedThemeTagValues]);

  const themeTagsWithSearch = useMemo(
    () => (searchValue ? searchedTags : groupedThemeTags),
    [searchValue, searchedTags, groupedThemeTags],
  );

  const handleSelectGroupedOptions = (values: string[]) => {
    setSelectedThemeTagValues(values);
  };

  const handleSearchThemeTags = useCallback(
    (value: string) => {
      setSearchValue(value);

      const filteredTagsBySearchValue: ComboGroupedElement[] =
        getFilteredTagsBySearchValue(groupedThemeTags, value);

      setSearchedTags(filteredTagsBySearchValue);
    },
    [groupedThemeTags],
  );

  const handleClearSelectedOptions = () => {
    setSelectedThemeTagValues([]);
    setSearchValue("");
    setSearchedTags([]);
  };

  return {
    selectedThemeTags: selectedThemeTagValues,
    handleSelectGroupedOptions,
    handleClearSelectedOptions,
    groupedThemeTags: themeTagsWithSearch,
    themeTagsFilterGames,
    themeTagsSearchQuery: searchValue,
    handleSearchThemeTags,
  };
};
